import React, { FC, useEffect, useMemo, useRef } from 'react';

import {
  Button,
  Center,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  Text,
} from '@chakra-ui/react';

import { MdNoteAdd } from 'react-icons/md';

import { CSVLink } from 'react-csv';

import { createColumnHelper } from '@tanstack/react-table';

import { UserPill } from 'components/core/UserPill';
import { Filters } from 'components/primitives/Filters';
import { DataTable, ServerSort } from 'components/modules/DataTable';
import { AddImpactReportModal } from 'components/modules/AddImpactReport';
import { EditImpactReportModal } from 'components/modules/EditImpactReport';

import { ImpactReportViewProps } from './ImpactReport.props';
import ImpactReportPdf from 'components/pdfs/ImpactReportPdf';
import { washHub } from 'store/slices';
import CopyAsCSv from 'components/primitives/CopyAsCSv';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import PdfGenerator from 'components/primitives/PdfGenerator';
import DeleteReportMenuItem from './DeleteReportMenuItem';
import { FaEllipsisV } from 'react-icons/fa';
import { ImpactReport } from 'types/report';
import {
  buildEnhancedImpactReport,
  buildImpactReportColumns,
} from './impactReportColumns';

const actionsCol = createColumnHelper<ImpactReport>();

const ImpactReportView: FC<ImpactReportViewProps> = (props) => {
  const {
    mutate,
    venue,
    startDate,
    endDate,
    csvData,
    reports,
    isLoading,
    pagination,
    setEndDate,
    setStartDate,
    isLoadingCSV,
    reportId,
    siteId,
    sortHandler,
    onApplyFilters,
    onClearFilters,
  } = props;

  const csvRef = useRef<any>(null);
  const addImpactReportModal = useRef<any>(null);
  const editImpactReportModal = useRef<any>(null);
  const selectedWashHubId = washHub.selectors.useSelectedWashHubId();

  useEffect(() => {
    if (reportId) {
      addImpactReportModal?.current?.onOpen?.(reportId, siteId);
    }
  }, [addImpactReportModal?.current, reportId, siteId]);

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    pagination.setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    pagination.setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isLoadingCSV}
          ml={2}
          minW="200px"
          onClick={() => {
            mutate(undefined, {
              onSuccess: () => {
                setTimeout(() => {
                  csvRef.current?.link?.click?.();
                }, 500);
              },
            });
          }}>
          Export Report to CSV
        </Button>
        {!isLoadingCSV && Boolean(csvData) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`impact-report.csv`}
            target="_blank"
          />
        )}
      </>
    );
  };

  const columns = useMemo(
    () => [
      ...buildImpactReportColumns(),
      actionsCol.display({
        id: 'actions',
        header: '',
        minSize: 130,
        size: 130,
        meta: { truncate: false },
        cell: ({ row: { original: report } }) => {
          const enhancedReport = buildEnhancedImpactReport(report);

          const data: any[] = [];

          data.push(
            [],
            [
              report!.impact?.wd.toString(),
              report!.impact?.co2m.toString(),
              report!.impact?.cos.toString(),
              report!.impact?.er.toString(),
              report!.impact?.wc.toString(),
            ],
            [],
          );

          return (
            <Flex
              justifyContent="center"
              alignItems="center"
              gridGap={3}
              onClick={(e) => e.stopPropagation()}>
              <CopyAsCSv data={data} showIcon={true} />
              <PdfGenerator
                fileName={`Impact Report - ${report?.site?.name} - ${report?.impact?.name}.pdf`}
                documentTemplate={ImpactReportPdf}
                data={enhancedReport}
              />
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<FaEllipsisV />}
                  aria-label="Options"
                  variant="ghost"
                />
                <MenuList>
                  <DeleteReportMenuItem inventoryReportId={report?._id} />
                </MenuList>
              </Menu>
            </Flex>
          );
        },
      }),
    ],
    [],
  );

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Impact Report
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            {renderExpBtn()}
            <Button
              isDisabled={selectedWashHubId === ''}
              mr={2}
              minW="100px"
              leftIcon={<MdNoteAdd size="20px" />}
              onClick={() => {
                addImpactReportModal?.current?.onOpen?.();
              }}>
              Add Impact Report
            </Button>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>

      <AddImpactReportModal ref={addImpactReportModal} />
      <EditImpactReportModal ref={editImpactReportModal} />

      <Flex mt="20px" direction="column">
        <DateRangeInputExt
          startDate={startDate}
          endDate={endDate}
          onSearchClear={onSearchClear}
          onSearchPress={onSearchPress}
        />
        <Flex direction="row" justifyContent={'space-between'}>
          <Filters
            venue={venue}
            isAccountManagersOnly
            isSitesOnly
            isFullButton
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
          />
        </Flex>
      </Flex>
      <DataTable<ImpactReport>
        mt="10px"
        minW="1096px"
        data={reports}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onSortChange={(s: ServerSort) => sortHandler(s.field)}
        onRowClick={(report) => {
          editImpactReportModal?.current?.onOpen?.(
            buildEnhancedImpactReport(report),
          );
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Impact Reports available</Text>
          </Center>
        )}
      />
    </Flex>
  );
};

export default ImpactReportView;
